import { useMemo, type FC, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  Label,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type {
  PaidMediaOverviewGranularity,
  PaidMediaOverviewSummaryPlatform,
  PaidMediaOverviewTrendPoint,
} from "src/services/PaidMediaOverviewAPI";
import {
  formatOverviewCurrency,
  formatOverviewMultiplier,
  formatOverviewText,
} from "../../utils/PaidMediaOverviewFormat";
import CustomChartTooltip from "src/components/reusable/CustomChartTooltip/CustomChartTooltip";
import {
  OverviewChartEmptyState,
  OverviewChartLoadingState,
  OverviewMetricTip,
} from "./OverviewPrimitives";
import {
  OVERVIEW_BENCHMARK_COLOR,
  OVERVIEW_GOOGLE_COLOR,
  OVERVIEW_GRID_COLOR,
  OVERVIEW_META_COLOR,
  OVERVIEW_SPIKE_COLOR,
  formatOverviewBucketLabel,
  overviewAxisProps,
} from "./OverviewChartConfig";
import { usePaidMediaChartRtl } from "../../utils/PaidMediaChartRtl";

const CPA_SPIKE_THRESHOLD = 1.1;

interface EfficiencyChartRow {
  date: string;
  metaRoas: number | null;
  googleRoas: number | null;
  metaCpa: number | null;
  googleCpa: number | null;
}

interface EfficiencyCpaRow extends EfficiencyChartRow {
  metaSpike: number | null;
  googleSpike: number | null;
}

const spikeValue = (current: number | null, previous: number | null): number | null => {
  if (current === null || previous === null || !Number.isFinite(current) || !Number.isFinite(previous)) {
    return null;
  }
  return current > previous * CPA_SPIKE_THRESHOLD ? current : null;
};

interface EfficiencyRowProps {
  series: PaidMediaOverviewTrendPoint[];
  platforms: PaidMediaOverviewSummaryPlatform[];
  granularity: PaidMediaOverviewGranularity;
  roasBenchmark: number | null;
  currency: string | null;
  loading: boolean;
}

const EfficiencyRow: FC<EfficiencyRowProps> = ({
  series,
  platforms,
  granularity,
  roasBenchmark,
  currency,
  loading,
}) => {
  const { t, i18n } = useTranslation();
  const chartRtl = usePaidMediaChartRtl();

  const meta = platforms?.find(entry => entry?.platform === "meta_ads");
  const google = platforms?.find(entry => entry?.platform === "google_ads");

  const data: EfficiencyChartRow[] = useMemo(
    () =>
      (series ?? []).map(point => ({
        date: formatOverviewBucketLabel(
          point?.date ?? "",
          granularity,
          date => t("paidMediaOverview.trend.weekOf", { date }),
          i18n.language
        ),
        metaRoas: point?.meta_ads?.roas ?? null,
        googleRoas: point?.google_ads?.roas ?? null,
        metaCpa: point?.meta_ads?.cost_per_purchase ?? null,
        googleCpa: point?.google_ads?.cost_per_purchase ?? null,
      })),
    [series, granularity, t, i18n.language]
  );

  const cpaData: EfficiencyCpaRow[] = useMemo(
    () =>
      data.map((row, index) => {
        const previous = index > 0 ? data[index - 1] : row;
        return {
          ...row,
          metaSpike: spikeValue(row.metaCpa, previous.metaCpa),
          googleSpike: spikeValue(row.googleCpa, previous.googleCpa),
        };
      }),
    [data]
  );

  const hasBenchmark = roasBenchmark !== null && Number.isFinite(roasBenchmark);
  const benchmarkLabel = t("paidMediaOverview.efficiency.benchmarkLabel", {
    value: formatOverviewMultiplier(roasBenchmark, 1),
  });

  const renderChart = (content: ReactElement) => {
    if (loading) {
      return <OverviewChartLoadingState />;
    }
    if (!data.length) {
      return <OverviewChartEmptyState />;
    }
    return <ResponsiveContainer>{content}</ResponsiveContainer>;
  };

  return (
    <div className="paid-media-overview__two-col-grid">
      <article className="paid-media-panel">
        <div className="paid-media-overview__panel-header">
          <div>
            <h3>
              {t("paidMediaOverview.efficiency.roasTitle")}
              <OverviewMetricTip
                id="roas"
                label={t("paidMediaOverview.efficiency.roasTitle")}
              />
            </h3>
            <p className="paid-media-overview__panel-subtitle">
              {t("paidMediaOverview.efficiency.roasSubtitle", {
                value: formatOverviewMultiplier(roasBenchmark, 1),
              })}
            </p>
          </div>
          <div className="paid-media-overview__legend-inline">
            <span className="paid-media-overview__legend-inline-item paid-media-overview__legend-inline-item--meta">
              {t("paidMediaOverview.efficiency.metaAverage", {
                value: meta?.formatted_roas ?? formatOverviewMultiplier(meta?.roas),
              })}
            </span>
            <span className="paid-media-overview__legend-inline-item paid-media-overview__legend-inline-item--google">
              {t("paidMediaOverview.efficiency.googleAverage", {
                value: google?.formatted_roas ?? formatOverviewMultiplier(google?.roas),
              })}
            </span>
          </div>
        </div>
        <div className="paid-media-overview__chart paid-media-overview__chart--sm">
          {renderChart(
            <LineChart data={data} margin={chartRtl.margin({ top: 5, right: 14, bottom: 5, left: -10 })}>
              <CartesianGrid stroke={OVERVIEW_GRID_COLOR} vertical={false} />
              <XAxis dataKey="date" reversed={chartRtl.axisReversed} {...overviewAxisProps} />
              <YAxis
                tickFormatter={value => `${value}x`}
                orientation={chartRtl.yAxisOrientation}
                {...overviewAxisProps}
              />
              <Tooltip
                wrapperClassName="paid-media-overview__chart-tooltip-wrapper"
                content={
                  <CustomChartTooltip
                    formatTooltip={(value: number) => formatOverviewMultiplier(value)}
                  />
                }
              />
              {hasBenchmark ? (
                <ReferenceLine
                  y={roasBenchmark as number}
                  stroke={OVERVIEW_BENCHMARK_COLOR}
                  strokeDasharray="4 4"
                  ifOverflow="extendDomain"
                >
                  <Label
                    value={benchmarkLabel}
                    position={chartRtl.labelPosition}
                    className="benchmark-reference-label"
                  />
                </ReferenceLine>
              ) : null}
              <Line
                type="monotone"
                dataKey="metaRoas"
                name={t("paidMediaOverview.efficiency.seriesMetaRoas")}
                stroke={OVERVIEW_META_COLOR}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="googleRoas"
                name={t("paidMediaOverview.efficiency.seriesGoogleRoas")}
                stroke={OVERVIEW_GOOGLE_COLOR}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
            </LineChart>
          )}
        </div>
      </article>

      <article className="paid-media-panel">
        <div className="paid-media-overview__panel-header">
          <div>
            <h3>
              {t("paidMediaOverview.efficiency.cpaTitle")}
              <OverviewMetricTip
                id="costPerPurchase"
                label={t("paidMediaOverview.efficiency.cpaTitle")}
              />
            </h3>
            <p className="paid-media-overview__panel-subtitle">
              {t("paidMediaOverview.efficiency.cpaSubtitle")}
            </p>
          </div>
          <div className="paid-media-overview__legend-inline">
            <span className="paid-media-overview__legend-inline-item paid-media-overview__legend-inline-item--meta">
              {t("paidMediaOverview.efficiency.metaAverage", {
                value: formatOverviewText(meta?.formatted_cost_per_purchase),
              })}
            </span>
            <span className="paid-media-overview__legend-inline-item paid-media-overview__legend-inline-item--google">
              {t("paidMediaOverview.efficiency.googleAverage", {
                value: formatOverviewText(google?.formatted_cost_per_purchase),
              })}
            </span>
          </div>
        </div>
        <div className="paid-media-overview__chart paid-media-overview__chart--sm">
          {renderChart(
            <LineChart data={cpaData} margin={chartRtl.margin({ top: 5, right: 10, bottom: 5, left: -10 })}>
              <CartesianGrid stroke={OVERVIEW_GRID_COLOR} vertical={false} />
              <XAxis dataKey="date" reversed={chartRtl.axisReversed} {...overviewAxisProps} />
              <YAxis
                tickFormatter={value => formatOverviewCurrency(value, currency)}
                orientation={chartRtl.yAxisOrientation}
                {...overviewAxisProps}
              />
              <Tooltip
                wrapperClassName="paid-media-overview__chart-tooltip-wrapper"
                content={({ active, payload, label }) => (
                  <CustomChartTooltip
                    active={active}
                    label={label}
                    payload={payload?.filter(
                      item =>
                        item?.dataKey !== "metaSpike" &&
                        item?.dataKey !== "googleSpike"
                    )}
                    formatTooltip={(value: number) =>
                      formatOverviewCurrency(value, currency, { compact: false })
                    }
                  />
                )}
              />
              <Line
                type="monotone"
                dataKey="metaCpa"
                name={t("paidMediaOverview.efficiency.seriesMetaCpa")}
                stroke={OVERVIEW_META_COLOR}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="googleCpa"
                name={t("paidMediaOverview.efficiency.seriesGoogleCpa")}
                stroke={OVERVIEW_GOOGLE_COLOR}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="metaSpike"
                stroke={OVERVIEW_SPIKE_COLOR}
                strokeWidth={0}
                dot={{ r: 4, fill: OVERVIEW_SPIKE_COLOR }}
                legendType="none"
                tooltipType="none"
              />
              <Line
                type="monotone"
                dataKey="googleSpike"
                stroke={OVERVIEW_SPIKE_COLOR}
                strokeWidth={0}
                dot={{ r: 4, fill: OVERVIEW_SPIKE_COLOR }}
                legendType="none"
                tooltipType="none"
              />
            </LineChart>
          )}
        </div>
      </article>
    </div>
  );
};

export default EfficiencyRow;
